import React, { useEffect, useMemo, useState } from "react";
import {
  ActionIcon,
  NumberInput,
  Select,
  Text,
  TextInput,
} from "@mantine/core";
import { IconTrash } from "@tabler/icons";
import { useTranslation } from "react-i18next";
import DataTable from "../dataTable";

const filterLines = (lines = [], searchText = "") => {
  const query = searchText.trim().toLowerCase();
  if (!query) return lines;
  return lines.filter(
    (line) =>
      line.accountCode?.toLowerCase().includes(query) ||
      line.accountName?.toLowerCase().includes(query) ||
      line.description?.toLowerCase().includes(query)
  );
};

const OpeningBalanceView = ({
  lines = [],
  accountOptions = [],
  loading = false,
  onUpdateLine,
  onRemoveLine,
}) => {
  const { t } = useTranslation();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [searchText, setSearchText] = useState("");

  const columns = useMemo(
    () => [
      {
        field: "accountCode",
        headerName: "accounting.journal.account",
        sortable: true,
        cellRender: (col, data) => (
          <Select
            size="xs"
            data={accountOptions}
            value={data.accountCode || null}
            onChange={(value) => {
              const account = accountOptions.find((item) => item.value === value);
              onUpdateLine(data._rowIndex, "accountCode", value);
              onUpdateLine(data._rowIndex, "accountName", account?.accountName || "");
            }}
            searchable
            placeholder={t("accounting.openingBalance.selectAccount")}
            nothingFound={t("No results found")}
          />
        ),
      },
      {
        field: "debit",
        headerName: "Debit",
        sortable: true,
        cellRender: (col, data) => (
          <NumberInput
            size="xs"
            precision={2}
            min={0}
            value={data.debit}
            onChange={(value) => onUpdateLine(data._rowIndex, "debit", value)}
          />
        ),
      },
      {
        field: "credit",
        headerName: "Credit",
        sortable: true,
        cellRender: (col, data) => (
          <NumberInput
            size="xs"
            precision={2}
            min={0}
            value={data.credit}
            onChange={(value) => onUpdateLine(data._rowIndex, "credit", value)}
          />
        ),
      },
      {
        field: "description",
        headerName: "Description",
        sortable: true,
        cellRender: (col, data) => (
          <TextInput
            size="xs"
            value={data.description}
            onChange={(e) =>
              onUpdateLine(data._rowIndex, "description", e.currentTarget.value)
            }
            placeholder={t("accounting.openingBalance.lineDescription")}
          />
        ),
      },
      {
        field: "_actions",
        headerName: "",
        sortable: false,
        cellRender: (col, data) => (
          <ActionIcon
            variant="subtle"
            color="gray"
            disabled={lines.length <= 2}
            onClick={() => onRemoveLine(data._rowIndex)}
            title={t("accounting.openingBalance.removeLine")}
          >
            <IconTrash size={16} />
          </ActionIcon>
        ),
      },
    ],
    [accountOptions, lines.length, onRemoveLine, onUpdateLine, t]
  );

  const filteredRows = useMemo(
    () => filterLines(lines, searchText),
    [lines, searchText]
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [searchText, lines.length]);

  const pagedRows = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredRows.slice(start, start + pageSize).map((line, index) => ({
      ...line,
      _id: line.accountCode || `line-${line._rowIndex ?? index}`,
    }));
  }, [filteredRows, currentPage, pageSize]);

  if (loading && lines.length === 0) {
    return (
      <Text size="sm" color="dimmed" py="md">
        {t("accounting.openingBalance.loadingAccounts")}
      </Text>
    );
  }

  if (!loading && lines.length === 0) {
    return (
      <Text size="sm" color="dimmed" py="md">
        {t("accounting.journal.noAccounts")}
      </Text>
    );
  }

  return (
    <DataTable
      data={pagedRows}
      columns={columns}
      hidePagination={filteredRows.length <= pageSize}
      hideSearchBar={false}
      searchText={searchText}
      onSearchChange={setSearchText}
      padding="md"
      pageSize={pageSize}
      currentPage={currentPage}
      onPageChange={setCurrentPage}
      onPageSizeChange={(size) => {
        setPageSize(Number(size) || 50);
        setCurrentPage(1);
      }}
      pagination={{
        total: filteredRows.length,
        totalPage: Math.max(1, Math.ceil(filteredRows.length / pageSize)),
        page: currentPage,
      }}
    />
  );
};

export default OpeningBalanceView;
